import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as _ from 'lodash';
import { Button, Col, Row, Popconfirm, Icon } from 'antd';
import { ActionBar } from 'Components/PromoLocali/styled';
import SearchProductsModal from 'Components/PromoLocali/SearchProductsModal';
import { Wrapper } from 'Common/styled';
import api from 'Common/api';
import PropTypes from 'prop-types';
import AppAccess from 'Common/appAccess';
import {
  handleServerSuccess,
  handleServerError,
  feedbackNotification,
} from 'Common/utils';
import { ROLE_CS, ROLE_GUEST } from 'Common/constants/global';
import AgTable from 'Components/AgTable/AgTable';
import {
  PL_PREV_VMM_NULL_PROMO,
  PL_SPT_NULL_PROMO,
  PL_SPT_GT_PREV_VMM,
} from 'Common/constants/product-errors';
import { colors } from 'Common/constants';
import moment from 'moment';

const DEFAULT_STATES = ['AC'];

const PromoLocali = forwardRef((props, ref) => {
  const { dossier, user, onProductCountChange } = props;

  const agTable = useRef(null);

  // AgTable builds its column definitions once on mount, so the `editable`
  // callbacks would otherwise capture a stale `dossier` and keep cells editable
  // after the promo is confirmed in-session. Reading from this ref keeps the
  // editability checks in sync with the latest dossier state.
  const dossierRef = useRef(dossier);
  dossierRef.current = dossier;

  const reportProductCount = useCallback((count) => {
    if (typeof onProductCountChange === 'function') {
      onProductCountChange(count);
    }
  }, [onProductCountChange]);

  const countGridRows = useCallback(() => {
    const gridApi = agTable.current && agTable.current.state && agTable.current.state.gridApi;

    if (!gridApi) {
      return 0;
    }

    let count = 0;
    gridApi.forEachNode(() => { count++; });
    return count;
  }, []);

  const [visibleColumns, setVisibleColumns] = useState([]);
  const [canSave, setCanSave] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [recordChanges, setRecordChanges] = useState({});
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);

  const getColumns = useCallback((dossierParam = dossier) => {
    const columns = [
      {
        title: 'REF LM',
        dataIndex: 'referenceId',
        key: 'referenceId',
        searchable: true,
        searchMultiple: true,
        sortable: true,
        align: 'center',
        width: 150,
        onHeaderCell: () => ({ style: { minWidth: '150px' } }),
        onCell: () => ({ style: { minWidth: '150px' } }),
      },
      {
        title: 'DESCRIZIONE',
        dataIndex: 'description',
        key: 'description',
        searchable: true,
        sortable: true,
        align: 'center',
        width: 250,
        onHeaderCell: () => ({ style: { minWidth: '250px' } }),
        onCell: () => ({ style: { minWidth: '250px' } }),
      },
      {
        title: 'SPT',
        cleanTitle: 'SPT',
        dataIndex: 'stock',
        editable: () => {
          const currentDossier = dossierRef.current;

          if (currentDossier.pxLoaded) {
            return false;
          }

          const diffBetweenTodayAndInstallationTo = moment(currentDossier.installationTo).diff(moment(), 'days');

          if (
            user.role === ROLE_CS
            && currentDossier.stato === 'DRAFT'
            && moment().isSameOrBefore(moment(currentDossier.installationTo), 'day')
            && diffBetweenTodayAndInstallationTo >= 15
          ) {
            return true;
          }

          return false;
        },
        contentType: 'integer',
        minValue: () => 0,
        align: 'center',
        onHeaderCell: () => ({ style: { minWidth: '115px' } }),
        onCell: () => ({ style: { minWidth: '115px' } }),
        width: 115,
        warning: (value) => value > 100,
        warningText: 'Attenzione: valore di facing superiore a 100',
        searchable: true,
      },
      {
        title: 'PREVISIONE VENDUTO',
        dataIndex: 'prevVmm',
        key: 'prevVmm',
        sortable: true,
        searchable: true,
        editable: () => {
          const currentDossier = dossierRef.current;
          const diffBetweenTodayAndInstallationTo = moment(currentDossier.installationTo).diff(moment(), 'days');
          return (
            user.role === ROLE_CS
            && currentDossier.stato === 'DRAFT'
            && moment().isSameOrBefore(moment(currentDossier.installationTo), 'day')
            && diffBetweenTodayAndInstallationTo >= 15
          );
        },
        contentType: 'decimal',
        precision: 2,
        align: 'center',
        removeSpinner: true,
        width: 130,
        visibleTo: [ROLE_CS, ROLE_GUEST],
        onHeaderCell: () => ({ style: { minWidth: '130px' } }),
        onCell: () => ({ style: { minWidth: '130px' } }),
      },
      {
        title: 'NOTE CS',
        editable: () => user.role === ROLE_CS && dossierRef.current.stato === 'DRAFT',
        dataIndex: 'note',
        contentType: 'note',
        align: 'center',
        hidden: !dossierParam.idStore,
        width: 100,
        onHeaderCell: () => ({ style: { minWidth: '100px' } }),
        onCell: () => ({ style: { minWidth: '100px' } }),
      },
    ];

    return columns.filter((column) => {
      if (column.visibleTo && !column.visibleTo.includes(user.role)) {
        return false;
      }

      if (column.hidden === true) {
        return false;
      }

      return true;
    });
  }, [dossier, user]);

  const getAllProducts = useCallback(() => {
    const gridApi = agTable.current && agTable.current.state && agTable.current.state.gridApi;

    if (!gridApi || _.isNil(dossier)) {
      return;
    }

    gridApi.showLoadingOverlay();

    api.promoLocali.getProducts(dossier.id).then((response) => {
      const products = (response.data || []).map((product) => ({ ...product, errors: product.errors || [] }));
      agTable.current.loadData(products);
      reportProductCount(products.length);
      gridApi.hideOverlay();
    }).catch(() => {
      gridApi.hideOverlay();
    });
  }, [dossier, reportProductCount]);

  const handleColumnVisibilityChange = useCallback((fieldName, isVisible) => {
    setVisibleColumns((prev) => {
      const updated = [...prev];

      if (isVisible && !updated.includes(fieldName)) {
        updated.push(fieldName);
      } else if (!isVisible && updated.includes(fieldName)) {
        updated.splice(updated.indexOf(fieldName), 1);
      }

      return updated;
    });
  }, []);

  const validateProduct = useCallback((change) => {
    const node = change.node;
    const errors = [];

    if (user.role === ROLE_CS) {
      if (node.data.prevVmm == null) {
        errors.push(PL_PREV_VMM_NULL_PROMO);
      }

      if (node.data.stock == null) {
        errors.push(PL_SPT_NULL_PROMO);
      }

      if (
        node.data.stock != null
        && node.data.prevVmm != null
        && Number(node.data.stock) > Number(node.data.prevVmm)
      ) {
        errors.push(PL_SPT_GT_PREV_VMM);
      }
    }

    node.setDataValue('errors', errors);
  }, [user.role]);

  const getIncompleteProductWarnings = useCallback(() => [], []);

  const saveRecords = useCallback(() => {
    agTable.current.state.gridApi.showLoadingOverlay();

    api.promoLocali.upsertProducts(dossier.id, Object.keys(recordChanges).map((key) => recordChanges[key].data)).then((response) => {
      const updates = [];
      const updatedRecords = (response.data || []).map((record) => ({ ...record, errors: record.errors || [] }));
      const changedRowIds = [];
      const updatedRecordChanges = { ...recordChanges };

      for (const recordChangeKey in recordChanges) {
        if (!recordChanges.hasOwnProperty(recordChangeKey)) {
          continue;
        }

        const recordChange = recordChanges[recordChangeKey];
        const updatedRecord = updatedRecords.find((r) => r.referenceId === recordChange.data.referenceId);

        if (updatedRecord) {
          const data = recordChange.data;

          for (const key in updatedRecord) {
            if (!updatedRecord.hasOwnProperty(key) || !data.hasOwnProperty(key)) {
              continue;
            }

            data[key] = updatedRecord[key];
          }

          if (Array.isArray(data.errors) && data.errors.length > 0) {
            changedRowIds.push(recordChangeKey);
          } else {
            delete updatedRecordChanges[recordChangeKey];
          }

          updates.push(data);
        } else {
          delete updatedRecordChanges[recordChangeKey];
        }
      }

      agTable.current.state.gridApi.applyTransaction({ update: updates });
      agTable.current.state.gridApi.redrawRows(Object.keys(recordChanges).map((key) => recordChanges[key]));
      agTable.current.cleanChangedRowIds(changedRowIds);
      setRecordChanges(updatedRecordChanges);

      const productsWithErrors = updatedRecords.filter((record) => record.errors.length > 0).length;
      let incompleteProducts = 0;

      agTable.current.state.gridApi.forEachNode((node) => {
        if (getIncompleteProductWarnings(node.data).length > 0) {
          incompleteProducts++;
        }
      });

      if (productsWithErrors === 0 && incompleteProducts === 0) {
        handleServerSuccess();
      }

      if (productsWithErrors > 0) {
        const message = productsWithErrors === 1
          ? 'C\'è una referenza non salvata a causa di errori'
          : `Ci sono ${productsWithErrors} referenze non salvate a causa di errori`;

        feedbackNotification('warning', 'Operazione parziale', message);
      }

      if (incompleteProducts > 0) {
        const message = incompleteProducts === 1
          ? 'C\'è una referenza ancora da lavorare'
          : `Ci sono ${incompleteProducts} referenze ancora da lavorare`;

        feedbackNotification(null, 'Referenze incomplete', message, <Icon type="warning" style={{ color: colors.yellow }} />);
      }
    }, handleServerError);
  }, [dossier, getIncompleteProductWarnings, recordChanges]);

  const deleteRecords = useCallback(() => {
    agTable.current.state.gridApi.showLoadingOverlay();

    Promise.all(selectedNodes.map((node) => api.promoLocali.deleteProduct(dossier.id, node.data.id)))
      .then(() => {
        agTable.current.state.gridApi.applyTransaction({
          remove: [...selectedNodes.map((node) => node.data)],
        });
        reportProductCount(countGridRows());
        agTable.current.state.gridApi.hideOverlay();
        handleServerSuccess();
      })
      .catch((error) => {
        agTable.current.state.gridApi.hideOverlay();
        handleServerError(error);
      });
  }, [dossier, selectedNodes, reportProductCount, countGridRows]);

  const handleAddProducts = useCallback((selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) {
      return;
    }

    const existingReferenceIds = new Set();

    if (agTable.current && agTable.current.state && agTable.current.state.gridApi) {
      agTable.current.state.gridApi.forEachNode((node) => {
        if (node.data && node.data.referenceId != null) {
          existingReferenceIds.add(String(node.data.referenceId));
        }
      });
    }

    const payload = selectedProducts
      .filter((product) => !existingReferenceIds.has(String(product.id)))
      .map((product) => ({
        referenceId: String(product.id),
        description: product.description,
      }));

    if (payload.length === 0) {
      feedbackNotification('info', 'Nessuna referenza aggiunta', 'Le referenze selezionate sono già presenti');
      setIsSearchModalVisible(false);
      return;
    }

    setIsAddingProducts(true);

    api.promoLocali.addProducts(dossier.id, payload).then(() => {
      handleServerSuccess();
      setIsSearchModalVisible(false);
      getAllProducts();
    }, handleServerError).finally(() => setIsAddingProducts(false));
  }, [dossier, getAllProducts]);

  const onAgTableChange = useCallback((change) => {
    switch (change.action) {
      case 'cellChange':
        if (user.role === ROLE_CS) {
          if (
            change.colDef.field === 'stock'
            && (change.newValue === 0 || change.newValue === null || change.newValue === undefined || change.newValue === '')
            && change.node.data.prevVmm !== change.newValue
          ) {
            change.node.setDataValue('prevVmm', change.newValue);
          }

          if (
            change.colDef.field === 'prevVmm'
            && (change.newValue === 0 || change.newValue === null || change.newValue === undefined || change.newValue === '')
            && change.node.data.stock !== change.newValue
          ) {
            change.node.setDataValue('stock', change.newValue);
          }
        }

        if (change.colDef && change.colDef.field !== 'errors') {
          validateProduct(change);
        }

        setRecordChanges((prev) => {
          const nextChanges = {
            ...prev,
            [change.node.id]: change.node,
          };

          const hasErrors = Object.values(nextChanges).some(
            (node) => node.data.errors && node.data.errors.length > 0,
          );
          setCanSave(!hasErrors);

          return nextChanges;
        });

        break;

      case 'selectionChange':
        setSelectedNodes([...change.nodes]);
        break;

      case 'gridLoaded':
        getAllProducts();
        break;

      case 'columnVisibilityChange':
        handleColumnVisibilityChange(change.column.colId, change.column.visible);
        break;

      default:
        break;
    }
  }, [getAllProducts, handleColumnVisibilityChange, recordChanges, user.role, validateProduct]);

  useEffect(() => {
    const columns = getColumns(dossier).filter((column) => column.hidden !== true).map((column) => column.dataIndex);
    setVisibleColumns(columns);
  }, [dossier, getColumns]);

  // The `editable` callbacks read live dossier state via `dossierRef`, so ag-grid
  // already blocks/allows edits correctly as soon as the dossier changes.
  // ag-grid's `lm-editable` cell styling is only recomputed when it decides to
  // refresh a cell (row data change, sort, filter, etc.), so it stays stale
  // otherwise: force a refresh here to keep it in sync with editability.
  useEffect(() => {
    const gridApi = agTable.current && agTable.current.state && agTable.current.state.gridApi;

    if (gridApi) {
      gridApi.refreshCells({ force: true });
    }
  }, [dossier.stato, dossier.pxLoaded, dossier.installationTo]);

  useImperativeHandle(ref, () => ({
    getColumns,
    getAllProducts,
    agTable,
    state: {
      recordChanges,
    },
  }), [getAllProducts, getColumns, recordChanges]);

  let extraStyles = {};

  if (fullscreen) {
    extraStyles = {
      width: '100%',
      height: '100%',
      position: 'fixed',
      backgroundColor: '#ffffff',
      top: 0,
      left: 0,
      padding: '25px 25px 0 25px',
      zIndex: 999,
    };
  }

  return (
    <Wrapper style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', ...extraStyles }}>
      <ActionBar>
        <Row gutter={[16, 16]}>
          <Col xs={{ span: 22, offset: 1 }} sm={{ span: 12, offset: 0 }}>
            {AppAccess.isAuthorized('add_ref_dossier', { stato: dossier.stato, type: dossier.type }) && (
              <Popconfirm
                title="Le modifiche non salvate andranno perse"
                onConfirm={() => setIsSearchModalVisible(true)}
                okText="OK"
                cancelText="Annulla"
                disabled={Object.keys(recordChanges).length === 0}
              >
                <Button
                  type="primary"
                  icon="file-add"
                  onClick={Object.keys(recordChanges).length === 0 ? () => setIsSearchModalVisible(true) : null}
                >
                  Aggiungi
                </Button>
              </Popconfirm>
            )}
            {AppAccess.isAuthorized('edit_dossier', { stato: dossier.stato, type: dossier.type, storeId: dossier.idStore }) && (
              <Button
                icon="save"
                disabled={Object.keys(recordChanges).length === 0 || !canSave}
                onClick={saveRecords}
              >
                Salva
              </Button>
            )}
            {AppAccess.isAuthorized('del_ref_dossier', { stato: dossier.stato, type: dossier.type }) && (
              <Popconfirm
                title="Sei sicuro di voler eliminare le referenze selezionate?"
                onConfirm={deleteRecords}
                okText="Sì"
                cancelText="No"
                disabled={selectedNodes.length === 0}
              >
                <Button icon="delete" disabled={selectedNodes.length === 0}>
                  Elimina
                </Button>
              </Popconfirm>
            )}
          </Col>
          <Col xs={{ span: 22, offset: 1 }} sm={{ span: 12, offset: 0 }}>
            <Button
              icon={fullscreen ? 'fullscreen-exit' : 'fullscreen'}
              onClick={() => setFullscreen((prev) => !prev)}
            />
            {dossier.pxLoaded && (
              <Button icon="file-image" onClick={() => window.open(dossier.planogramA, '_blank')}>
                Visualizza planogramma
              </Button>
            )}
          </Col>
        </Row>
      </ActionBar>
      <Row style={{ flexGrow: 1, display: 'flex', flexDirection: 'row' }}>
        <Col style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <AgTable
            canMakeSelection={
              AppAccess.isAuthorized('mass_ref_dossier', { stato: dossier.stato, type: dossier.type })
              || AppAccess.isAuthorized('del_ref_dossier', { stato: dossier.stato, type: dossier.type })
            }
            ref={agTable}
            columns={getColumns(dossier)}
            emitChange={onAgTableChange}
            getIncompleteProductWarnings={getIncompleteProductWarnings}
          />
        </Col>
      </Row>
      <SearchProductsModal
        visible={isSearchModalVisible}
        dossierId={dossier.id}
        onCancel={() => setIsSearchModalVisible(false)}
        onAdd={handleAddProducts}
        isAdding={isAddingProducts}
        productStates={DEFAULT_STATES}
        disableProductStates
      />
    </Wrapper>
  );
});

PromoLocali.displayName = 'PromoLocali';

PromoLocali.propTypes = {
  dossier: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onProductCountChange: PropTypes.func,
};

export default PromoLocali;
